import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getCampaignMessageReport, getCampaignMessageFullReport } from '../../../lib/api';
import { downloadCSV } from '../utils/downloadCSV';
import { ERROR_CODE_MESSAGES } from '../../../constants/errorCodeMessages';
import './summary.css';
import './detailsReport.css';

const DetailsReport = () => {
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => iso(today), [today]);
  const defaultTo = useMemo(() => iso(today), [today]);

  // Draft filters (what the user is typing)
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [status, setStatus] = useState('all');
  const [campaignId, setCampaignId] = useState('');
  const [recipientNumber, setRecipientNumber] = useState('');

  // Applied filters (what the report list is currently showing)
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    fromDate: defaultFrom,
    toDate: defaultTo,
    status: 'all',
    campaignId: '',
    recipientNumber: '',
  }));
  const [reportData, setReportData] = useState([]);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total_pages: 1 });
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const buildRequestBody = (filters) => {
    const body = {
      from_date: filters.fromDate,
      to_date: filters.toDate,
    };

    if (filters.status && filters.status !== 'all') {
      body.status = filters.status;
    }

    if (filters.campaignId) {
      const parsed = parseInt(filters.campaignId, 10);
      if (Number.isFinite(parsed)) body.campaign_id = parsed;
    }

    if (filters.recipientNumber) {
      body.recipient_number = filters.recipientNumber.toString().trim();
    }

    return body;
  };

  const fetchData = async ({ nextPage, replace = false, ignoreHasMore = false, filters = appliedFilters }) => {
    if (!filters?.fromDate || !filters?.toDate) {
      setError('Please select both from and to dates.');
      return;
    }
    if (loading || (!ignoreHasMore && !hasMore)) return;
    setLoading(true);
    setError('');
    try {
      const body = buildRequestBody(filters);
      const resp = await getCampaignMessageReport(body, nextPage, limit);
      const newData = Array.isArray(resp?.data) ? resp.data : [];
      
      if (replace) {
        setReportData(newData);
      } else if (newData.length) {
        setReportData((prev) => [...prev, ...newData]);
      }
      
      setPagination(resp.pagination || { total_pages: 1 });

      const currentPage = resp?.pagination?.current_page ?? nextPage;
      const totalPages = resp?.pagination?.total_pages;
      if (Number.isFinite(totalPages) && Number.isFinite(currentPage)) {
        setHasMore(currentPage < totalPages);
      } else {
        setHasMore(newData.length >= limit);
      }
      
      if (isInitialLoad) setIsInitialLoad(false);
    } catch (e) {
      setError('Failed to fetch report.');
    } finally {
      setLoading(false);
    }
  };

  const onSearch = () => { 
    const nextFilters = {
      fromDate,
      toDate,
      status,
      campaignId: campaignId.toString().trim(),
      recipientNumber: recipientNumber.toString().trim(),
    };

    setAppliedFilters(nextFilters);
    pageRef.current = 1;
    setPage(1);
    setHasMore(true);
    setIsInitialLoad(true);
    setReportData([]);
    containerRef.current?.scrollTo?.({ top: 0 });
    fetchData({ nextPage: 1, replace: true, ignoreHasMore: true, filters: nextFilters });
  };

  const onScroll = () => {
    const el = containerRef.current;
    if (!el || loading || !hasMore) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (nearBottom) {
      const next = pageRef.current + 1;
      pageRef.current = next;
      setPage(next);
      fetchData({ nextPage: next, replace: false, ignoreHasMore: false });
    }
  };

  useEffect(() => { 
    const initial = {
      fromDate: defaultFrom,
      toDate: defaultTo,
      status: 'all',
      campaignId: '',
      recipientNumber: '',
    };
    setAppliedFilters(initial);
    pageRef.current = 1;
    setPage(1);
    fetchData({ nextPage: 1, replace: true, ignoreHasMore: true, filters: initial });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownload = async () => {
    try {
      const body = buildRequestBody(appliedFilters);
      setDownloading(true);
      setDownloadProgress(0);
      const full = await getCampaignMessageFullReport(body);
      if (!full.length) { alert('No data to download.'); setDownloading(false); return; }
      await downloadCSV(full, 'campaign_report.csv', {
        onProgress: (p) => setDownloadProgress(p),
      });
    } catch (e) {
      console.error('Download error:', e);
      alert('Failed to download report.');
    } finally {
      setDownloading(false);
    }
  };

  const renderChat = (row) => {
    try {
      const tpl = typeof row.code === 'string' ? JSON.parse(row.code) : row.code;
      if (!tpl?.components) return <div className="chat-bubble error">No preview</div>;
      const header = tpl.components.find((c) => c.type === 'HEADER')?.text || '';
      const body = tpl.components.find((c) => c.type === 'BODY')?.text || '';
      const footer = tpl.components.find((c) => c.type === 'FOOTER')?.text || '';
      return (
        <div className="chat-bubble">
          {header && <div className="chat-header">{header}</div>}
          <div className="chat-body">{body}</div>
          {footer && <div className="chat-footer">{footer}</div>}
        </div>
      );
    } catch {
      return <div className="chat-bubble error">Invalid JSON</div>;
    }
  };

  const parseErrorCell = (row) => {
    if (!row.errors) return 'N/A';
    try {
      const parsed = typeof row.errors === 'string' ? JSON.parse(row.errors) : row.errors;
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      const code = first?.code || first?.error_code || first?.title;
      const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
      return ERROR_CODE_MESSAGES[codeNum] || first?.message || first?.title || 'Error occurred';
    } catch {
      return 'Invalid error format';
    }
  };

  return (
    <div className="summary-wrap">
      <div className="summary-header">
        <h2>Detail Message Report</h2>
        <div className="summary-meta">{reportData.length} results</div>
      </div>

      <div className="filter-bar">
        <div className="field">
          <label>From</label>
          <input className="filt-control" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input className="filt-control" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Status</label>
          <select className="filt-control" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="read">Read</option>
            <option value="failed">Failed</option>
            <option value="delivered">Delivered</option>
            <option value="created">Created</option>
          </select>
        </div>
        <div className="field">
          <label>Campaign ID</label>
          <input className="filt-control" type="text" placeholder="Optional" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} />
        </div>
        <div className="field">
          <label>Recipient</label>
          <input className="filt-control" type="text" placeholder="10 digits" value={recipientNumber} onChange={(e) => setRecipientNumber(e.target.value)} />
        </div>
        <div className="actions filter-bar-actions">
          <button className="btn btn-primary" onClick={onSearch}>Search</button>
          <button className="btn" style={{ border: '1px solid var(--border)' }} onClick={onDownload} disabled={downloading}>
            {downloading ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </div>

      {downloading && (
        <div className="download-progress">
          <div className="bar" style={{ width: `${downloadProgress}%` }} />
          <span>{downloadProgress}%</span>
        </div>
      )}

      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

      <div ref={containerRef} className="summary-scroll" onScroll={onScroll}>
        <div className="summary-table-wrap">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Campaign ID</th>
                <th>Recipient</th>
                <th>Status</th>
                <th>Sent At</th>
                <th>Updated At</th>
                <th>Chat Preview</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoad && loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="skeleton-row">
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-pill" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-bubble" /></td>
                    <td><div className="sk sk-text long" /></td>
                  </tr>
                ))
              ) : (
                <>
                  {reportData.length > 0 ? (
                    reportData.map((row, idx) => (
                      <tr key={idx}>
                        <td data-label="Campaign ID">{row.campaign_id}</td>
                        <td data-label="Recipient">{row.recipient_id}</td>
                        <td data-label="Status">{row.status}</td>
                        <td data-label="Sent At">{row.message_sent_at ? new Date(row.message_sent_at).toLocaleString() : '—'}</td>
                        <td data-label="Updated At">{row.message_updated_at ? new Date(row.message_updated_at).toLocaleString() : '—'}</td>
                        <td data-label="Chat Preview">{renderChat(row)}</td>
                        <td data-label="Error">{parseErrorCell(row)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={7} className="empty-cell">No results</td></tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {!isInitialLoad && loading && (
          <div className="summary-loading-bottom">
            <div className="loading-spinner-small"></div>
            <span>Loading more...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailsReport;
